import AwesomeDebouncePromise from "awesome-debounce-promise";
import axios, { AxiosError } from "axios";
import classNames from "classnames";
import { SERVER_URL } from "Constants";
import { observer } from "mobx-react";
import {
	esignceremonystatus,
	esignuserstatus,
	esignwitnessstatus,
	esignworkflowstage,
	esignworkflowstageOptions,
	esignworkflowstatus,
	esignworkflowstatusOptions
} from "Models/Enums";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { DEFAULT_PAGE_LENGTH } from "Util/Constants";
import EntityList from "Views/Components/EntityList/EntityList";
import TablePagination from "Views/Components/Pagination/TablePagination";
import RequestWrap, { GetRefreshKey } from "Views/Components/RequestWrap/RequestWrap";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { TextFieldSetter } from "Views/Components/TextBox/TextFieldSetter";
import { ESignEnvelopeDetails } from "./ESignEnvelopeDetails";

type ESignEnevlopeListResponse = {
	data: ESignWorkflowSummary[];
	count: number;
}

export type ESignWorkflowSummary = {
	id: string;
	created: Date;
	modified: Date;
	status: esignworkflowstatus;
	stage: esignworkflowstage;
	owner?: ESignEnvelopOwnerDetail;
	signatoryCount: number;
	rejectionCount: number;
	notificationEmail: string;
	template: ESignTemplate;
	signees: ESignSignee[];
	base64SignedContract: string;
}

type ESignTemplate = {
	name: string;
	lifeSpanInDays: number;
	minimumNumberOfSignees: number;
	requiresWitness: boolean;
}

type ESignEnvelopOwnerDetail = {
	appId: string;
	name: string;
	email?: string;
	phone?: string;
}

export type ESignSignee = {
	status: esignceremonystatus;
	ipAddress: string;
	location: string;
	signatureTimeStamp?: Date;
	created: Date;
	modified: Date;
	witness: ESignWitness;
} & ESignUser;

type ESignWitness = {
	status: esignwitnessstatus;
	ipAddress: string;
	location: string;
	signatureTimeStamp: Date;
	created: Date;
	modified: Date;
} & ESignUser;

type ESignUser = {
	userName: string;
	userEmail: string;
	userPhone: string;
	userStatus: esignuserstatus;
	userCreated: Date;
	userModified: Date;
}

function DateTimeExpiryItem(date?: Date, daysFromExpiry?: number) {
	if (!date || !daysFromExpiry) {
		return '-';
	}
	const expiryDate = moment(date).utc().add(daysFromExpiry, 'days');
	const now = moment().utc();
	if (now.isAfter(expiryDate)) {
		return <span style={{color: "#DF1A26"}}>Expired</span>;
	}
	return expiryDate.fromNow();
}

export const fetchESignEnvelopes = async (
	businessEntityId: string, 
	search: string,
	sortColumn: string,
	sortDescending: boolean,
	pageNo: number
): Promise<ESignEnevlopeListResponse> => {
	try {
		const response = await axios.get(
			`${SERVER_URL}/api/esign/business-entity/${businessEntityId}/workflow`,
			{
				params: {
					search: search,
					sortColumn: sortColumn,
					sortDescending: sortDescending,
					pageNo: pageNo,
					pageSize: DEFAULT_PAGE_LENGTH
				}
			}
		);
		return response.data;
	} catch (error) {
		throw error as AxiosError;
	}
}

const fetchESignEnvelopesDebounced = AwesomeDebouncePromise(async (businessEntityId: string, search: string, sortColumn: string, sortDescending: boolean, pageNo: number) => {
	return fetchESignEnvelopes(businessEntityId, search, sortColumn, sortDescending, pageNo);
}, 400);

type Props = {
	businessEntityId: string;
	refresh: boolean;
	reset: () => void;
};

export const ESignEnevlopeList: React.FunctionComponent<Props> = observer(({ businessEntityId, refresh, reset }) => {
	const [search, setSearch] = useState('');
	const [pageNo, setPageNo] = useState(0);
	const [selectedEnvelopeSummary, setSelectedEnvelopeSummary] = useState<ESignWorkflowSummary | null>(null);
	const [sortColumn, setSortColumn] = useState('dateCreated');
	const [sortDescending, setSortDescending] = useState(true);

	const onSortChange = (header: string) => {
		setSortColumn(header);
		setSortDescending(sortColumn === header ? !sortDescending : false);
	};


	const refreshKey = useMemo(() => {
		return GetRefreshKey(
			search,
			pageNo,
			sortColumn, 
			sortDescending,
			businessEntityId,
			refresh,
		);
	}, [search, pageNo, sortColumn, sortDescending, businessEntityId, refresh]);

	return (
		<>
			<div className="search-container">
				<div className="searchbar search">
					<TextFieldSetter
						className={classNames('search-input search__collection')}
						value={search}
						setValue={setSearch}
						label=""
						onAfterChange={() => setPageNo(0)}
						labelVisible={false}
						placeholder="Search by Name/Email Address/App ID"
					/>
				</div>
			</div>
			<div className="collection-component">
				<RequestWrap
					refreshKey={refreshKey}
					request={() => {
						if (refresh){
							reset();
						}
						return fetchESignEnvelopesDebounced(businessEntityId, search, sortColumn, sortDescending, pageNo + 1);
					}}
					loadingContent={<InlineSpinner />}
					errorContent={
						<div className="error-msg">
							<h3>No Workflow found for the provided entity</h3>
						</div>
					}
				>
					{(response: ESignEnevlopeListResponse) => (
						<>
							<EntityList
								collection={response.data}
								columns={[
									{
										displayName: 'App ID',
										columnName: 'appId',
										value: summary => summary.owner?.appId ?? '-',
									},
									{
										displayName: 'Document Name',
										columnName: 'templateName',
										value: summary => summary.template.name,
										sortable: true,
										sortClicked: () => {
											onSortChange('templateName');
										},
									},
									{
										displayName: 'Date Created',
										columnName: 'dateCreated',
										value: summary => moment(summary.created).format('HH:mm DD/MM/YYYY'),
										sortable: true,
										sortClicked: () => {
											onSortChange('dateCreated');
										},
									},
									{
										displayName: 'Last Modified',
										columnName: 'lastModified',
										value: summary => moment(summary.modified).format('HH:mm DD/MM/YYYY'),
										sortable: true,
										sortClicked: () => {
											onSortChange('lastModified');
										},
									},
									{
										displayName: 'Marked',
										columnName: 'status',
										value: summary => esignworkflowstatusOptions[summary.status],
										sortable: true,
										sortClicked: () => {
											onSortChange('status');
										},
									},
									{
										displayName: 'Last Activity',
										columnName: 'stage',
										value: summary => esignworkflowstageOptions[summary.stage],
										sortable: true,
										sortClicked: () => {
											onSortChange('stage');
										},
									},
									{
										displayName: 'Signed By',
										columnName: 'signatoryCount',
										value: summary => summary.signatoryCount,
										sortable: true,
										sortClicked: () => {
											onSortChange('signatoryCount');
										},
									},
									{
										displayName: 'Rejected By',
										columnName: 'rejectionCount',
										value: summary => summary.rejectionCount,
										sortable: true,
										sortClicked: () => {
											onSortChange('rejectionCount');
										},
									},
									{
										displayName: 'Expiring',
										columnName: 'expiry',
										value: summary => DateTimeExpiryItem(summary.created, summary.template.lifeSpanInDays),
										sortable: true,
										sortClicked: () => {
											onSortChange('expiry');
										},
									}
								]}
								idColumn="id"
								onClickRow={summary => setSelectedEnvelopeSummary(summary)}
								sortColumn={sortColumn}
								sortDescending={sortDescending}
							/>
							<section className="collection__load">
								<TablePagination
									perPage={DEFAULT_PAGE_LENGTH}
									pageNo={pageNo}
									totalRecords={response.count}
									onPageChange={setPageNo}
								/>
							</section>
						</>
					)}
				</RequestWrap>
			</div>
			{selectedEnvelopeSummary && (
				<ESignEnvelopeDetails
					workflowSummary={selectedEnvelopeSummary}
					onClose={() => setSelectedEnvelopeSummary(null)}
				/>
			)}
		</>
	)
});